import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Grid,
} from '@material-ui/core'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import IconButton from '@material-ui/core/IconButton'
import AddCircleRoundedIcon from '@material-ui/icons/AddCircleRounded'
import { makeStyles } from '@material-ui/core/styles'
import { Translate } from 'react-localize-redux'
import { track } from 'utils/segmentTracking'
import LoadingSpinner from 'components/loader/LoadingSpinner'
import useTeamFeature from 'hooks/useTeamFeature'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import { fetchMatchHighlights } from 'api/match-highlights/api'
import MatchHighlightsDialog from './MatchHighlightsDialog'
import MatchHighlightsList from './MatchHighlightsList'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const useStyles = makeStyles(() => ({
  card: {
    boxShadow: 'unset',
  },
  cardContent: {
    maxHeight: '500px',
    overflowY: 'auto',
    paddingTop: '0',
  },
}))

const MatchHighlights = ({
  currentMatch,
  participants,
}) => {
  const classes = useStyles()
  const [open, setOpen] = useState(false)
  const [highlights, setHighlights] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const featureGuard = useFeatureAccessGuard()

  const { enabled: hasAdvancedMatchFeaturesEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.advanced_match_features_enable
  )

  const loadHighlights = async (matchUuid, showLoading = true) => {
    if (!matchUuid) return
    try {
      if (showLoading) setIsLoading(true)
      const response = await fetchMatchHighlights(matchUuid)
      setHighlights(response.data || [])
    } catch (error) {
      console.error('Failed to load highlights:', error)
    } finally {
      if (showLoading) setIsLoading(false)
    }
  }

  const reloadHighlights = (matchUuid) => loadHighlights(matchUuid, false)

  const handleClickOpen = () => {
    if (featureGuard(hasAdvancedMatchFeaturesEnabled)) return

    setOpen(true)

    // Segment tracking
    track('WClick_MPHighlights_Add', {
      category: 'MP_Highlights',
    })
  }
  const handleClose = () => {
    setOpen(false)
  }

  const handleCreateHighlight = (newHighlight) => {
    setHighlights(prev => [...prev, newHighlight])
    if (currentMatch) {
      reloadHighlights(currentMatch.uuid)
    }
  }

  const handleUpdateHighlight = (updatedHighlight) => {
    setHighlights(prev => prev.map(h => h.uuid === updatedHighlight.uuid ? updatedHighlight : h))
    if (currentMatch) {
      reloadHighlights(currentMatch.uuid)
    }
  }

  const handleDeleteHighlight = (uuid) => {
    setHighlights(prev => prev.filter(h => h.uuid !== uuid))
    if (currentMatch) {
      reloadHighlights(currentMatch.uuid)
    }
  }

  const participantsList = useMemo(
    () =>
      participants.map((p) => {
        const { player } = p.relationships

        return {
          value: player.uuid,
          title: `${player.first_name} ${player.last_name}`,
        }
      }),
    [participants]
  )
  useEffect(() => {
    if (currentMatch) {
      loadHighlights(currentMatch.uuid)
    }
  }, [currentMatch])

  return (
    <Translate>
      {({ translate }) => (
        <>
          {open && currentMatch && (
            <MatchHighlightsDialog
              onClose={handleClose}
              participantsList={participantsList}
              open={open}
              matchUuid={currentMatch.uuid}
              onHighlightCreated={handleCreateHighlight}
            />
          )}
          <Card className={classes.card}>
            <CardHeader
              action={
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleClickOpen}
                    endIcon={
                      !hasAdvancedMatchFeaturesEnabled ? (
                        <LockIconComponent context="primaryButton" />
                      ) : null
                    }
                  >
                    {translate('matches.highlights.add-highlight')}
                  </Button>
                </div>
              }
              title={
                <div>
                  <Grid container spacing={1}>
                    <Grid item xs="auto">
                      <Typography variant="h4" component="h4">
                        {translate('matches.highlights.title')}
                      </Typography>
                    </Grid>
                  </Grid>
                </div>
              }
            />
            <CardContent className={classes.cardContent}>
              {isLoading && <LoadingSpinner />}
              {!isLoading && (
                <Grid item xs={12} md={12}>
                  {highlights && currentMatch && (
                    <MatchHighlightsList
                      participantsList={participantsList}
                      highlights={highlights}
                      onUpdateHighlight={handleUpdateHighlight}
                      onDeleteHighlight={handleDeleteHighlight}
                    />
                  )}
                  {!highlights.length > 0 && (
                    <Typography
                      variant="body2"
                      color="primary"
                      style={{ textAlign: 'center' }}
                      onClick={handleClickOpen}
                    >
                      <IconButton size="small" color="primary">
                        <AddCircleRoundedIcon />
                      </IconButton>
                      {translate(
                        'matches.highlights.start-adding-match-highlight'
                      )}
                    </Typography>
                  )}
                </Grid>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Translate>
  )
}
MatchHighlights.propTypes = {
  participants: PropTypes.arrayOf(PropTypes.shape().isRequired).isRequired,
  currentMatch: PropTypes.shape().isRequired,
}

export default connect(
  ({ matches: { current } }) => ({
    currentMatch: current,
    participants: current?.relationships?.participants || [],
  })
)(MatchHighlights)
